import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchRolesList,
  fetchSubmodulePermissions,
  saveSubmodulePermissions,
} from "../../api/userBuilderApi";
import { isProtectedAdminUserManagementRow, isProtectedPortalRoleUser } from "../../../../public/utils/roleAccess";
import PermissionSubmodulePanels from "../permissions/PermissionSubmodulePanels";
import PermissionsWorkspace from "../permissions/PermissionsWorkspace";

export default function RoleSubmodulePermissionsSection({ onNotify }) {
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadRoles = useCallback(async () => {
    try {
      const r = await fetchRolesList(1, 200);
      setRoleOptions(
        (r.rows || [])
          .filter((row) => !isProtectedPortalRoleUser(row))
          .map((row) => ({
            value: String(row.id),
            label: row.role_name,
          })),
      );
    } catch (e) {
      onNotify?.(e.message || "Could not load roles.", { variant: "error" });
    }
  }, [onNotify]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const loadMatrix = useCallback(async () => {
    const rid = Number(selectedRoleId);
    if (!rid) {
      setRows([]);
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const data = await fetchSubmodulePermissions(rid);
      setRows(
        (data || []).map((row) => ({
          ...row,
          _rowKey: `s-${row.submodule_id}`,
          _label: row.submodule_name,
          _sublabel: row.submodule_slug,
        })),
      );
    } catch (e) {
      setRows([]);
      setLoadError(e.message || "Could not load submodule permissions.");
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  useEffect(() => {
    const onModulesSaved = () => loadMatrix();
    window.addEventListener("ub-module-permissions-saved", onModulesSaved);
    return () =>
      window.removeEventListener("ub-module-permissions-saved", onModulesSaved);
  }, [loadMatrix]);

  const tableRows = useMemo(() => rows, [rows]);
  const roleId = Number(selectedRoleId);

  const isRowDisabled = useCallback(
    (row) => isProtectedAdminUserManagementRow(roleId, row),
    [roleId],
  );

  const onToggle = (rowKey, permKey, checked) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row._rowKey !== rowKey) return row;
        if (isRowDisabled(row)) return row;
        return { ...row, [permKey]: checked ? 1 : 0 };
      }),
    );
  };

  const onPatchRow = (rowKey, patch) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row._rowKey !== rowKey) return row;
        if (isRowDisabled(row)) return row;
        return { ...row, ...patch };
      }),
    );
  };

  const onPatchColumn = (permKey, turnOn, rowDisabled) => {
    setRows((prev) =>
      prev.map((row) => {
        if (rowDisabled?.(row)) return row;
        return { ...row, [permKey]: turnOn ? 1 : 0 };
      }),
    );
  };

  const onGrantAllView = () => {
    setRows((prev) =>
      prev.map((row) => {
        if (isRowDisabled(row)) return row;
        return { ...row, can_view: 1 };
      }),
    );
  };

  const onClearAll = () => {
    setRows((prev) =>
      prev.map((row) => {
        if (isRowDisabled(row)) return row;
        return {
          ...row,
          can_view: 0,
          can_create: 0,
          can_edit: 0,
          can_delete: 0,
        };
      }),
    );
  };

  const onSave = async () => {
    const rid = Number(selectedRoleId);
    if (!rid) {
      onNotify?.("Select a role first.", { variant: "error" });
      return;
    }
    setSaving(true);
    onNotify?.("");
    try {
      const payload = rows.map((row) => ({
        submodule_id: row.submodule_id,
        can_view: row.can_view,
        can_create: row.can_create,
        can_edit: row.can_edit,
        can_delete: row.can_delete,
      }));
      await saveSubmodulePermissions(rid, payload);
      onNotify?.("Submodule permissions saved.");
      await loadMatrix();
    } catch (e) {
      onNotify?.(e.message || "Could not save submodule permissions.", {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PermissionsWorkspace
      id="ub-submodule-permissions"
      variant="submodule"
      title="Screens & sidebar"
      lead="Enable Menu modules (User management) and Save first, then set screen access here and Save again. Saving screens also grants parent module view automatically."
      roleOptions={roleOptions}
      selectedRoleId={selectedRoleId}
      onRoleChange={setSelectedRoleId}
      rows={tableRows}
      loading={loading}
      saving={saving}
      loadError={loadError}
      onSave={onSave}
      onGrantAllView={onGrantAllView}
      onClearAll={onClearAll}
      emptyMatrixMessage="Enable one or more modules under Menu modules first, then assign screen access here."
    >
      {(filteredRows) => (
        <PermissionSubmodulePanels
          rows={filteredRows}
          nameColumn="Screen"
          onToggle={onToggle}
          onPatchRow={onPatchRow}
          onPatchColumn={onPatchColumn}
          disabled={!selectedRoleId || saving}
          isRowDisabled={isRowDisabled}
        />
      )}
    </PermissionsWorkspace>
  );
}
